import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LogOut } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { lifespan } from '../../shared/tree'
import { ConnectionChoices } from '@/components/ConnectionChoices'
import { FormError } from '@/components/FormError'
import { MySections } from '@/components/MySections'
import { ProfilePhoto } from '@/components/ProfilePhoto'
import { Initial } from '@/components/paper'
import { Avatar } from '@/components/tree/Avatar'
import { PersonForm } from '@/components/tree/PersonForm'
import { PortraitButton } from '@/components/tree/PortraitButton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, type Me } from '@/lib/api'
import { circleStyle } from '@/lib/circleColor'
import { useFamilies } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { signOut } from '@/lib/signOut'
import { usePerson, type MemorialFields, type PersonFields } from '@/lib/tree'
import { AdminSettings } from './AdminSettings'
import { Preferences } from './Preferences'
import { PageLoader } from '@/components/Loader'

type MyCircle = { id: string; name: string; personId: string | null }

// Your profile, from the profile menu: your photo and name, your page on each circle's tree (your details there, and
// your face where you haven't set a profile photo), and your preferences. The owner's Admin section sits at the end.
export function Profile({ me }: { me: Me }) {
  usePageFrame(undefined, { title: 'My profile', back: { to: '/', label: 'Back' } })
  const circles = useQuery({
    queryKey: ['me', 'people'],
    queryFn: () => api<{ circles: MyCircle[] }>('/me/people'),
    enabled: me.sections.people,
  })
  // In the Modern style of Home the owner can pick, the top reads like a profile: your face in a ring beside your
  // name, then your circles as rings that open them. The pages below are settings, the same in both.
  const modern = me.homeStyle === 'modern'
  return (
    <div className="flex flex-col gap-10">
      {modern ? (
        <ModernTop me={me} />
      ) : (
        <header className="flex flex-col items-center gap-3 text-center">
          <ProfilePhoto me={me} edit className="size-36 sm:size-44" />
          <h1 className="text-display text-balance">{me.name}</h1>
          <p className="text-muted-foreground">{me.email}</p>
          <Button variant="ghost" size="sm" className="max-nav:hidden" asChild>
            <Link to="/">Done</Link>
          </Button>
        </header>
      )}
      <YourName me={me} />
      {circles.data && circles.data.circles.length > 0 && <OnTheTree circles={circles.data.circles} />}
      <FormError error={circles.error} />
      <Preferences me={me} />
      <MySections me={me} />
      <ConnectionChoices />
      {me.admin && <AdminSettings />}
      <section className="border-t pt-8">
        <Button variant="outline" onClick={() => void signOut()}>
          <LogOut aria-hidden /> Sign out
        </Button>
      </section>
    </div>
  )
}

function ModernTop({ me }: { me: Me }) {
  const families = useFamilies()
  const circles = families.data?.families ?? []
  return (
    <header className="flex flex-col gap-6">
      <div className="flex items-center gap-5">
        <span className="mp-ring-o mp-cover shrink-0">
          <span>
            <ProfilePhoto me={me} fill />
          </span>
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-title font-semibold tracking-tight text-balance sm:text-display">{me.name}</h1>
          <p className="truncate text-sub text-muted-foreground">{me.email}</p>
        </div>
      </div>
      {circles.length > 0 && (
        <ul aria-label="Your circles" className="-mx-4 scroll-row gap-2 px-4 py-1 nav:-mx-2 nav:px-2">
          {circles.map((c, i) => (
            <li key={c.id} className="shrink-0 snap-start">
              <Link to={`/circle/${c.id}`} className="mp-ring" aria-label={`Open ${c.name}`}>
                <span
                  aria-hidden="true"
                  className="circle-ring m-1 grid size-[4.5rem] shrink-0 rounded-full"
                  style={circleStyle(c.color)}
                >
                  {/* "The Riveras" is an R, not a T. */}
                  <Initial name={c.name.replace(/^the\s+/i, '')} index={i} className="size-full text-2xl" />
                </span>
                <span className="w-full truncate text-xs text-muted-foreground">{c.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </header>
  )
}

// The name people see beside what you add. Your pages on the trees keep their own name, as the family wrote it.
function YourName({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (next: string) => api('/me', { method: 'PATCH', body: { name: next } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      setName(null)
    },
  })
  if (name === null)
    return (
      <section className="flex flex-col items-start gap-2" aria-labelledby="your-name">
        <h2 id="your-name" className="text-sm font-medium">
          Your name
        </h2>
        <p className="text-sub text-muted-foreground">Shown beside the recipes, photos and moments you add.</p>
        <Button variant="outline" size="sm" onClick={() => setName(me.name)}>
          Change your name
        </Button>
      </section>
    )
  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate(name)
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <div className="grid gap-2">
        <Label htmlFor="your-name-input">Your name</Label>
        <Input
          id="your-name-input"
          autoComplete="name"
          required
          maxLength={80}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="text-sub text-muted-foreground">Shown beside the recipes, photos and moments you add.</p>
      </div>
      <FormError error={save.error} />
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={() => setName(null)}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function OnTheTree({ circles }: { circles: MyCircle[] }) {
  return (
    <section className="flex flex-col gap-4 border-t pt-8" aria-labelledby="on-the-tree">
      <div className="flex flex-col gap-1.5">
        <h2 id="on-the-tree" className="text-title">
          {circles.length > 1 ? 'Your pages on the tree' : 'Your page on the tree'}
        </h2>
        <p className="text-sub text-muted-foreground">
          {circles.length > 1
            ? 'Each circle keeps its own page for you. Your face here is the newest photo on any of them.'
            : 'Your photo and details, as your circle sees them.'}
        </p>
      </div>
      <ul className="flex flex-col gap-4">
        {circles.map((circle) => (
          <li key={circle.id}>
            {circle.personId ? <YourPage id={circle.personId} circle={circle.name} /> : <NotOnTree circle={circle} />}
          </li>
        ))}
      </ul>
    </section>
  )
}

function NotOnTree({ circle }: { circle: MyCircle }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-3xl border border-border p-5">
      <p>
        You’re not on {circle.name}’s tree yet. Start with yourself on People, or say “This is me” on the page someone
        made for you.
      </p>
      <Button variant="outline" size="sm" asChild>
        <Link to={`/circle/${circle.id}/people`}>Go to People</Link>
      </Button>
    </div>
  )
}

// Your page in one circle: change its photo or details here, without going through People.
function YourPage({ id, circle }: { id: string; circle: string }) {
  const queryClient = useQueryClient()
  const person = usePerson(id)
  const [editing, setEditing] = useState(false)
  const familyId = person.data?.familyId
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['person', id] })
    await queryClient.invalidateQueries({ queryKey: ['me'] })
    if (!familyId) return
    await queryClient.invalidateQueries({ queryKey: ['tree', familyId] })
    await queryClient.invalidateQueries({ queryKey: ['family', familyId] })
  }
  const save = useMutation({
    mutationFn: (fields: PersonFields & Partial<MemorialFields>) =>
      api(`/people/${id}`, { method: 'PATCH', body: fields }),
    onSuccess: async () => {
      setEditing(false)
      await refresh()
    },
  })
  if (person.isPending) return <PageLoader />
  if (person.isError) return <FormError error={person.error} />
  const me = person.data
  const years = lifespan(me)
  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-border p-5 sm:p-6" aria-label={circle}>
      <div className="flex items-center gap-4">
        <Avatar person={me} className="size-16 text-2xl" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{me.name}</span>
          <span className="text-sub text-muted-foreground">
            {circle}
            {years && ` · ${years}`}
          </span>
        </div>
      </div>
      {editing ? (
        <div className="flex flex-col gap-4">
          <PersonForm
            initial={me}
            withNote
            label="Save"
            pending={save.isPending}
            error={save.error}
            onSave={(f) => save.mutate(f)}
          />
          <Button variant="ghost" className="self-start" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {me.canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                Edit details
              </Button>
              <PortraitButton person={me} onDone={refresh} />
            </>
          )}
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/person/${me.id}`}>Open your page</Link>
          </Button>
        </div>
      )}
    </section>
  )
}
